import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FileUp, FolderOpen } from "lucide-react";

interface DocsAwaitingMatter {
  matterId: string;
  title: string;
  clientName: string;
  pendingCount: number;
}

interface DocumentsAwaitingWidgetProps {
  matters: DocsAwaitingMatter[];
}

const DocumentsAwaitingWidget: React.FC<DocumentsAwaitingWidgetProps> = ({ matters }) => {
  const navigate = useNavigate();

  const totalPending = matters.reduce((sum, m) => sum + m.pendingCount, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 rounded-lg" style={{ backgroundColor: "#f59e0b1A" }}>
          <FileUp className="h-4 w-4" style={{ color: "#f59e0b" }} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-700 m-0 leading-none">Documents Awaiting Upload</h3>
        </div>
      </div>

      {/* Big number */}
      <div className="text-center mb-4">
        <span className="text-3xl font-bold text-slate-800">{totalPending}</span>
        <p className="text-xs text-slate-500 mt-1">
          pending across {matters.length} matter{matters.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Matter list */}
      {matters.length > 0 && (
        <div className="space-y-2">
          {matters.slice(0, 5).map((m, idx) => (
            <div
              key={m.matterId || idx}
              className="flex items-center justify-between p-2.5 rounded-lg bg-amber-50 border border-amber-100 hover:shadow-sm transition-shadow cursor-pointer"
              onClick={() => navigate(`/matters/${m.matterId}`)}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <FolderOpen className="h-3.5 w-3.5 flex-shrink-0 text-amber-600" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-700 truncate">{m.title}</p>
                  <p className="text-xs text-slate-500 truncate">{m.clientName}</p>
                </div>
              </div>
              <span className="text-xs font-bold text-amber-700 flex-shrink-0 ml-2">
                {m.pendingCount} pending
              </span>
            </div>
          ))}

          {matters.length > 5 && (
            <p className="text-xs text-center text-slate-400 pt-1">
              +{matters.length - 5} more matter{matters.length - 5 !== 1 ? "s" : ""}
            </p>
          )}
        </div>
      )}

      {/* Empty state */}
      {matters.length === 0 && (
        <div className="flex flex-col items-center py-4 text-center">
          <FileUp className="h-10 w-10 text-slate-300 mb-2" />
          <p className="text-sm font-medium text-slate-500">All caught up!</p>
          <p className="text-xs text-slate-400">No documents awaiting upload</p>
        </div>
      )}
    </motion.div>
  );
};

export default DocumentsAwaitingWidget;
